"use client";

import React, { useState } from 'react';
import Schema from './Schema';
import ImageWrapper from './ui/ImageWrapper';
import SectionDescription from "@/component/ui/SectionDescription/SectionDescription";

type FAQ = {
    id?: string;
    _id?: string;
    question: string;
    answer: React.ReactNode;
};


export type FAQItem = FAQ;

type Props = {
    items: FAQ[]; // required — caller must provide items
    title?: string;
    description?: string;
    multi?: boolean; // allow multiple open items when true
};

export default function FAQSection({ items, title, description, multi = false }: Props) {
    const FAQS = items ?? [];

    // Helper function to get unique key for each FAQ
    const getKey = (faq: FAQ, index: number): string => {
        return faq.id || faq._id || `faq-${index}`;
    };

    const firstKey = FAQS && FAQS.length ? getKey(FAQS[0], 0) : null;
    const [openMulti, setOpenMulti] = useState<string[]>(multi && firstKey ? [firstKey] : []);
    const [openSingle, setOpenSingle] = useState<string | null>(!multi && firstKey ? firstKey : null);

    if (!FAQS || FAQS.length === 0) return null;

    // Keep only one open at a time even when `multi` is true:
    // clicking an unopened item replaces the open list with that id,
    // clicking the open item closes it.
    const toggleMulti = (key: string) =>
        setOpenMulti((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [key]));

    const toggleSingle = (key: string) => setOpenSingle((prev) => (prev === key ? null : key));

    // removed expandAll / collapseAll controls per design requirement

    const faqSchema = FAQS && FAQS.length > 0 ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer
            },
        })),
    } : null;

    return (
        <>
            <Schema schema={faqSchema} />
            <section id="faqs" className="mt-3">
                <div className="py-4 mb-5 md:mb-8">
                    <div className="mb-6">
                        <h2
                            className="text-[20px] md:text-[20px] leading-[24px] font-semibold md:font-medium tracking-[0px] align-middle text-gray-900"
                            
                        >
                            {title ?? 'Frequently Asked Questions'}
                        </h2>
                        <SectionDescription text={description} className="mt-1" collapsible={false} />
                    </div>

                    {/* expand/collapse controls removed per design */}

                    <div className="space-y-4">
                        {FAQS.map((f, index) => {
                            const faqKey = getKey(f, index);
                            const isOpen = multi ? openMulti.includes(faqKey) : openSingle === faqKey;
                            const headerId = `faq-${faqKey}-header`;
                            const panelId = `faq-${faqKey}-panel`;
                            return (
                                <div key={faqKey} className="bg-white rounded-lg border border-gray-200">
                                    <h3>
                                        <button
                                            id={headerId}
                                            aria-controls={panelId}
                                            aria-expanded={isOpen}
                                            onClick={() => (multi ? toggleMulti(faqKey) : toggleSingle(faqKey))}
                                            className="w-full flex items-center justify-between p-5 text-left cursor-pointer focus:outline-none"
                                        >
                                            <div>
                                                <div className="text-[14px] leading-[20px] font-medium text-gray-900">{f.question}</div>
                                            </div>                                            
                                            <div
                                                className={`transition-transform duration-300 my-1 flex-shrink-0 ${
                                                    isOpen ? "rotate-180" : ""
                                                }`}
                                                >
                                                <ImageWrapper
                                                    src="/faq-drop-arrow.svg"
                                                    alt="Toggle FAQ icon"
                                                    width={12}
                                                    height={12}
                                                    cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL}
                                                    title="Toggle FAQ"
                                                />
                                            </div>
                                        </button>
                                    </h3>

                                    <div
                                        id={panelId}
                                        role="region"
                                        aria-labelledby={headerId}
                                        className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                                        style={{
                                            gridTemplateRows: isOpen ? '1fr' : '0fr',
                                        }}
                                    >
                                        <div className="overflow-hidden">
                                            <div
                                                className="px-5 pb-5 text-[14px] leading-[22.75px] font-normal text-gray-600 [&_a]:text-blue-600 [&_a:hover]:underline"
                                                
                                                {...(typeof f.answer === 'string'
                                                    ? { dangerouslySetInnerHTML: { __html: f.answer } }
                                                    : {})}
                                            >
                                                {typeof f.answer !== 'string' ? f.answer : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </>
    );
}
